import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useRoute } from 'react-router5';
import { getPrefixedRoute } from '@vmos2/core';
import { classNames } from '@vmos2/utils';
import styled from '@emotion/styled';

import { Icon } from '../icons';
import { withErrorBoundary } from './with-error-boundary';
import { useComponentTheme } from '../useComponentTheme';

type Props = {
  analyticsEvent?: Function,
  ariaLabel?: string,
  children?: React.Node,
  className?: string,
  dataTest?: string,
  disabled?: boolean,
  elemRef?: Function,
  level?: 1 | 2 | 3 | 4 | 5,
  onClick?: Function,
  options: { [index: string]: mixed },
  params?: { [index: string]: mixed },
  rel?: string,
  showExternalLinkIcon?: boolean,
  title?: string,
  to?: string,
  url?: string,
};

export const Link = ({
  analyticsEvent,
  ariaLabel,
  children,
  className,
  dataTest,
  disabled,
  elemRef,
  level,
  onClick,
  options = {},
  params = {},
  rel,
  showExternalLinkIcon,
  title,
  to,
  url,
}: Props) => {
  const componentTheme = useComponentTheme(theme => theme.components.Button, 'Link');
  const { route, router } = useRoute();
  const { t } = useTranslation('common');
  const toPrefixedRoute = to && getPrefixedRoute(to);
  const href = url || router.buildUrl(toPrefixedRoute, { ...route.params, ...params });
  const isActive = router.isActive(toPrefixedRoute, { ...params });

  const handleClick = useCallback(
    e => {
      e.preventDefault();

      if (disabled) {
        return;
      }

      analyticsEvent?.();
      onClick?.();

      if (url) {
        window.open(url, '_blank');
      } else {
        router.navigate(toPrefixedRoute, { ...route.params, ...params }, options);
      }
    },
    [disabled, toPrefixedRoute, params, options, onClick]
  );

  return (
    <A
      aria-disabled={disabled}
      aria-label={ariaLabel}
      className={classNames([className, isActive && 'active'])}
      componentTheme={componentTheme}
      data-test={dataTest || `${toPrefixedRoute}-link`}
      href={href}
      level={level}
      onClick={handleClick}
      ref={elemRef}
      rel={rel}
      title={title}
      {...(isActive && { 'aria-current': 'page' })}
    >
      {children}

      {url && showExternalLinkIcon && (
        <IconWrapper name="ArrowUpRightFromSquare" size="75%" title={t('linkExternalIconAriaLabel')} />
      )}
    </A>
  );
};

const A = styled.a`
  ${({ componentTheme, level }) => level && `font-size: ${componentTheme.fontSize[`level${level}`]};`}
  color: ${({ theme }) => theme.color.brand};
  text-decoration: ${({ componentTheme }) => componentTheme.textDecoration};
  text-transform: ${({ componentTheme }) => componentTheme.textTransform};
`;

const IconWrapper = styled(Icon)`
  margin-left: 0.5em;
`;

export default withErrorBoundary(() => <a href="/">Wrong URL</a>)(Link);
